import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./causes.css";

import AudioFloat from "./AudioFloat.js";
/**
 * Layout:
 * HERO (90vh) with title + toggles + scroll cue
 * └─ COPY (newspaper columns)
 * └─ VIEW SWITCH (grid | slide)
 * └─ GALLERY (grid or single-row slide)
 */

export default function Causes({ fetchPhotographs, photographs }) {
  const [selectedbtn, setSelectedBtn] = useState(3);    // 2: Street, 3: School, 4: Alone
  const [ready, setReady] = useState(false);
  const [viewMode, setViewMode] = useState("grid");     // "grid" | "slide"

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!photographs) fetchPhotographs && fetchPhotographs();
  }, [photographs, fetchPhotographs]);

  // All causes (for hero fallback)
  const allCausesItems = useMemo(() => {
    if (!photographs) return [];
    const out = [];
    Object.keys(photographs).forEach((key) => {
      const item = photographs[key] || {};
      const { cause, imageURL, order } = item;
      if (!cause || !imageURL) return;
      const raw = (order && (order.$numberInt ?? order.$numberLong)) ?? order;
      const ordNum = typeof raw === "string" ? parseInt(raw, 10) : raw ?? 999999;
      out.push({ key, ...item, __src: imageURL, __order: Number.isFinite(ordNum) ? ordNum : 999999 });
    });
    out.sort((a, b) => (a.__order - b.__order) || String(a.__src).localeCompare(String(b.__src)));
    return out;
  }, [photographs]);

  // Filter by tab
  const filteredItems = useMemo(() => {
    if (!photographs) return [];
    const out = [];
    Object.keys(photographs).forEach((key) => {
      const item = photographs[key] || {};
      const {
        cause, streetliving, alone, culturaldiversity, workshop,
        imageURL, order,
      } = item;
      if (!cause || !imageURL) return;
      const isSchoolUnderBridge = !workshop && !streetliving && !alone && !culturaldiversity;
      const showByTab =
        (selectedbtn === 2 && !!streetliving) ||
        (selectedbtn === 3 && isSchoolUnderBridge) ||
        (selectedbtn === 4 && !!alone);
      if (!showByTab) return;
      const raw = (order && (order.$numberInt ?? order.$numberLong)) ?? order;
      const ordNum = typeof raw === "string" ? parseInt(raw, 10) : raw ?? 999999;
      out.push({ key, ...item, __src: imageURL, __order: Number.isFinite(ordNum) ? ordNum : 999999 });
    });
    out.sort((a, b) => (a.__order - b.__order) || String(a.__src).localeCompare(String(b.__src)));
    return out;
  }, [photographs, selectedbtn]);

  // Hero image (random from tab → any causes → static)
  const FALLBACK_DESKTOP = "https://lorranlima.s3.us-east-2.amazonaws.com/causesbanner.jpg";
  const FALLBACK_MOBILE  = "https://lorranlima.s3.us-east-2.amazonaws.com/causesbannermob.jpg";

  const [heroUrl, setHeroUrl] = useState(null);
  useEffect(() => {
    const pool = filteredItems.length ? filteredItems : allCausesItems;
    if (pool.length) {
      const rand = pool[Math.floor(Math.random() * pool.length)];
      setHeroUrl(rand.__src);
    } else {
      setHeroUrl(null);
    }
  }, [filteredItems, allCausesItems]);

  // Slide pagination (single row)
  const isMobile = typeof window !== "undefined" &&
                   window.matchMedia &&
                   window.matchMedia("(max-width: 767px)").matches;
  const pageCap = isMobile ? 1 : 3;

  const pages = useMemo(() => {
    const result = [];
    for (let i = 0; i < filteredItems.length; i += pageCap) {
      result.push(filteredItems.slice(i, i + pageCap));
    }
    return result;
  }, [filteredItems, pageCap]);

  // Scroll target below hero (copy)
  const contentTopRef = useRef(null);
  const scrollToContent = () => {
    const el = contentTopRef.current;
    if (!el) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  };

  return (
    <main className={`causes-page ${ready ? "is-ready" : ""}`} data-light-nav="true" aria-labelledby="causes-title">
      {/* ===== HERO ===== */}
      <section className="cz-hero" aria-label="Causes hero">
        {/* Backgrounds for CSS swapping */}
        <div className="cz-hero-bg cz-hero-bg--desktop anim-bg" aria-hidden="true"
             style={{ backgroundImage: `url(${heroUrl || FALLBACK_DESKTOP})` }} />
        <div className="cz-hero-bg cz-hero-bg--mobile anim-bg" aria-hidden="true"
             style={{ backgroundImage: `url(${heroUrl || FALLBACK_MOBILE})` }} />
        {/* Hard image so it always shows */}
        <img className="cz-hero-img" src={heroUrl || FALLBACK_DESKTOP} alt="" loading="eager" decoding="async" fetchpriority="high" />
        {/* Dark overlay */}
        <div className="cz-hero-veil cz-hero-veil--dark anim-veil" aria-hidden="true" />

        <div className="cz-hero-center">
          <header className="causes-header" style={{ textAlign: "center" }}>
            <h1 id="causes-title" className="causes-hed fx-fade-up" style={{ "--d": "120ms" }}>
              Causes
            </h1>
            <p className="causes-dek fx-fade-up" style={{ "--d": "240ms" }}>
              Where awareness becomes action—meeting poverty where it lives.
            </p>
          </header>

          {/* TOGGLES (now inside hero, under title) */}
          <nav className="causes-toggles causes-toggles--in-hero" role="tablist" aria-label="Choose a cause">
            <button
              role="tab"
              aria-selected={selectedbtn === 2}
              className={`cause-toggle fx-pop ${selectedbtn === 2 ? "is-active" : ""}`}
              style={{ "--d": "320ms" }}
              onClick={() => setSelectedBtn(2)}
            >
              <span className="cause-label">Street Living</span>
              <span className="cause-sub">The City as Shelter</span>
            </button>
            <button
              role="tab"
              aria-selected={selectedbtn === 3}
              className={`cause-toggle fx-pop ${selectedbtn === 3 ? "is-active" : ""}`}
              style={{ "--d": "380ms" }}
              onClick={() => setSelectedBtn(3)}
            >
              <span className="cause-label">School Under The Bridge</span>
              <span className="cause-sub">Delhi — Free Learning</span>
            </button>
            <button
              role="tab"
              aria-selected={selectedbtn === 4}
              className={`cause-toggle fx-pop ${selectedbtn === 4 ? "is-active" : ""}`}
              style={{ "--d": "440ms" }}
              onClick={() => setSelectedBtn(4)}
            >
              <span className="cause-label">Alone</span>
              <span className="cause-sub">Childhood Without a Net</span>
            </button>
          </nav>

          {/* Bounce cue */}
          <button className="fp-hero__scroll-cue" onClick={scrollToContent} aria-label="Scroll to content">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
            </svg>
          </button>
        </div>
      </section>

      {/* ===== COPY (newspaper columns) ===== */}
      <section ref={contentTopRef} className="causes-copy">
        {selectedbtn === 2 && (
          <div className="news-cols">
            <p>Street living is one of the most painful realities I’ve ever witnessed. I remember being in India, seeing a beautiful family living on the streets with nothing but the love between them. They had no home, no possessions — just each other. And yet, they were laughing. Their eyes were full of light, and I stood there in disbelief. I was in a deep depression at the time, surrounded by comfort and choice, and still I felt empty. But they, with so little, seemed to hold the entire world in their hearts.</p>
            <p>In that moment, I learned something I will never forget. A friend once told me, “Your home is within you.” I never truly understood those words until I met that family. Their peace, their closeness, their ability to find joy in the middle of struggle — it changed me. They showed me that home is not a place, but a feeling.</p>
            <p>But it’s still heartbreaking. It breaks me to see how many people are forced to sleep on sidewalks, in corners, under the open sky — invisible to a world that walks past them. It’s painful to know that something as basic as shelter, warmth, and safety is still a luxury for so many.</p>
            <p>Through my photography, I try to give their stories a voice. Not to take their pain, but to make others feel — to see them as human, as equal, as part of us. Street living is not just about poverty; it’s about survival, dignity, and love in its purest form.</p>
            <p>These moments remind me of how fragile and how strong we are at the same time. The people I meet on the streets carry something many of us have lost — gratitude, unity, and an unbreakable spirit. They’ve taught me that sometimes those with the least can feel the most.</p>
            <p>Through my lens, I want the world to look closer — to see the beauty, the sadness, and the resilience that live together on those streets. Because behind every face, there is a story that deserves to be seen, felt, and remembered.</p>
          </div>
        )}

        {selectedbtn === 3 && (
          <div className="news-cols">
            <p>During my time in India, I visited a place that deeply moved me — a free school under the Delhi metro bridge. Beneath the noise and rush of the city, there’s a small open space where hope quietly lives. More than 300 children, all from nearby slums, come here to learn. For many of them, this is their only chance at an education — a chance to dream beyond survival.</p>
            <p>The school has no official building, no government support, and no walls — only painted blackboards on the concrete pillars that hold up the bridge. The space itself becomes the classroom, with the metro passing above as the lessons continue below. It’s run entirely by volunteers — seven teachers who give their time freely to help these children understand the school curriculum and, more importantly, to believe in themselves.</p>
            <p>Classes are divided into two shifts: boys study in the morning, and girls come in the afternoon. The simplicity of this place is humbling — no desks, no fancy materials, just a handful of people and their shared belief that every child deserves a chance to learn.</p>
            <p>Standing there, watching these children recite lessons, laugh, and raise their hands with excitement, I felt something powerful. Amid so little, there was joy. Amid struggle, there was dignity. It reminded me that education isn’t just about books — it’s about hope.</p>
            <p>This school may stand under a bridge, but it lifts hundreds of children above the limits that life has placed on them. It is a symbol of humanity — proof that even in the hardest places, people can come together to build light from nothing.</p>
          </div>
        )}

        {selectedbtn === 4 && (
          <div className="news-cols">
            <p>More than 30,000 children die every single day from poverty-related causes. That means a child is lost every three seconds — not to war, not to disease alone, but to neglect, hunger, and inequality. Poverty is silent, but it kills with devastating consistency. And its main victims are always the most innocent — the children.</p>
            <p>In my travels, I’ve seen children who live completely alone. Some are left behind because their parents must work endlessly just to survive. Others wander the streets, sleeping where they can, caring for themselves long before they ever had the chance to be children. Some are victims of violence or abuse, trapped in circumstances no child should ever endure.</p>
            <p>There is nothing more heartbreaking than seeing a child searching for safety in a world that has forgotten them. To see a little one holding the weight of survival instead of a toy — that’s an image that never leaves your heart.</p>
            <p>Through my camera, I try to give these children a voice — to show that they exist, that they matter, and that their stories deserve to be seen. Because behind every statistic, there is a name, a face, a heartbeat that once carried hope.</p>
            <p>Every child deserves love, protection, and a chance to dream. And until the world understands that, poverty will continue to steal what is most sacred — the light of our children.</p>
          </div>
        )}
      </section>

      {/* ===== VIEW SWITCH ===== */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div className="view-toggle" role="tablist" aria-label="View mode">
          <button
            className={`view-btn ${viewMode === "grid" ? "is-active" : ""}`}
            role="tab"
            aria-selected={viewMode === "grid"}
            onClick={() => setViewMode("grid")}
          >
            Grid
          </button>
          <button
            className={`view-btn ${viewMode === "slide" ? "is-active" : ""}`}
            role="tab"
            aria-selected={viewMode === "slide"}
            onClick={() => setViewMode("slide")}
          >
            Slide
          </button>
        </div>
      </div>

      {/* ===== GALLERY ===== */}
      <section className="causes-gallery">
        {viewMode === "grid" && (
          <div className="causes-grid cz-grid">
            {filteredItems.map((item) => (
              <div className="painting-image-case fade-in cz-card" key={item.key}>
                <div className="painting-image-inside">
                  <img
                    className="painting-image"
                    src={item.__src}
                    alt={item.title || "Cause photograph"}
                    loading="lazy"
                    decoding="async"
                    onLoad={(e) => e.currentTarget.classList.add("is-visible")}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === "slide" && <SlideRow pages={pages} isMobile={isMobile} />}
      </section>

      <footer className="causes-footer">
        <Link to="/humanitarianism" className="cz-back">Back to Humanitarianism</Link>
      </footer>
    </main>
  );
}

function SlideRow({ pages, isMobile }) {
  const carouselRef = useRef(null);
  const goPage = (dir) => {
    const el = carouselRef.current; if (!el) return;
    const pageW = el.clientWidth;
    const target = Math.round((el.scrollLeft + dir * pageW) / pageW) * pageW;
    el.scrollTo({ left: target, behavior: "smooth" });
  };
  return (
    <div className="cz-carousel-wrap">
      <button className="cz-page-nav left" aria-label="Previous group" onClick={() => goPage(-1)}>‹</button>
      <div className="cz-carousel" ref={carouselRef}>
        {pages.map((page, pi) => (
          <div className="cz-page" key={`p-${pi}`}>
            <div className="cz-grid cz-grid--page" style={{ gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)" }}>
              {page.map((item, idx) => (
                <div className="painting-image-case fade-in cz-card" key={`${item.key}-${pi}-${idx}`}>
                  <div className="painting-image-inside">
                    <img
                      className="painting-image"
                      src={item.__src}
                      alt={item.title || "Cause photograph"}
                      loading="lazy"
                      decoding="async"
                      onLoad={(e) => e.currentTarget.classList.add("is-visible")}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className="cz-page-nav right" aria-label="Next group" onClick={() => goPage(1)}>›</button>
      <div className="cz-swipe-cue" aria-hidden="true"><span>‹ ›</span> <em>Swipe</em></div>

              <AudioFloat
                    srcs={[
                      { src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/causes.mp3", type: "audio/mpeg" },
                      
                    ]}
                    startMuted={false}
                    startVolume={0.5}
                    loop={true}
                    label="Site background audio"
                    startTime = {20}
                    
                  />
    </div>
  );
}
